/**
 * MeetingsWidget Component
 *
 * Виджет для управления встречами с организацией.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Button,
  Box,
  Stack,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Avatar,
  Badge,
} from '@mantine/core';
import {
  IconCalendarEvent,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconClock,
  IconUser,
  IconCheck,
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import MeetingFormModal from './MeetingFormModal';
import MeetingViewModal from './MeetingViewModal';
import MeetingSummaryModal from './MeetingSummaryModal';
import tokens from '../../../theme/designTokens';

/**
 * API функции для встреч
 */
const meetingsApi = {
  async getMeetings(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/meetings`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить встречи');
    }
    return response.json();
  },

  async createMeeting(organizationId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/meetings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать встречу');
    }
    return response.json();
  },

  async updateMeeting(organizationId, meetingId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/meetings/${meetingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить встречу');
    }
    return response.json();
  },

  async addSummary(organizationId, meetingId, summary) {
    const response = await fetch(`/api/organizations/${organizationId}/meetings/${meetingId}/summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ summary }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось сохранить итоги');
    }
    return response.json();
  },

  async deleteMeeting(organizationId, meetingId) {
    const response = await fetch(`/api/organizations/${organizationId}/meetings/${meetingId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить встречу');
    }
  },

  async getContacts(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/contacts`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить контакты');
    }
    return response.json();
  },
};

/**
 * Форматирование полного имени контакта
 */
const formatContactName = (contact) => {
  if (!contact) return 'Неизвестный контакт';
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * Форматирование списка контактов
 */
const formatContactNames = (contacts) => {
  if (!contacts || contacts.length === 0) return 'Без контактов';
  if (contacts.length === 1) return formatContactName(contacts[0]);
  return `${formatContactName(contacts[0])} (+${contacts.length - 1})`;
};

/**
 * Форматирование списка участников
 */
const formatParticipantNames = (participants) => {
  if (!participants || participants.length === 0) return 'Не указаны';
  const names = participants.map(p => p.full_name || p.email);
  if (names.length === 1) return names[0];
  return `${names[0]} (+${names.length - 1})`;
};

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Получение инициалов
 */
const getInitials = (contact) => {
  if (!contact) return 'В';
  const first = contact.first_name?.[0]?.toUpperCase() || '';
  const last = contact.last_name?.[0]?.toUpperCase() || '';
  return last + first || first || 'В';
};

/**
 * Компонент отображения встречи
 */
const MeetingItem = ({ meeting, onView, onEdit, onDelete }) => {
  const contactNames = formatContactNames(meeting.contacts);
  const participantNames = formatParticipantNames(meeting.participants);
  const firstContact = meeting.contacts?.[0];

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[1])}
      onClick={() => onView(meeting)}
    >
      <Avatar size="md" radius="xl" color="blue">
        {getInitials(firstContact)}
      </Avatar>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {contactNames}
          </Text>
          {meeting.is_completed && (
            <Badge
              size="xs"
              color="green"
              variant="filled"
              radius="xl"
              leftSection={<IconCheck size={10} />}
            >
              Проведено
            </Badge>
          )}
          {meeting.edit_count > 0 && (
            <Badge size="xs" color="blue" variant="light" radius="xl">
              Изменено {meeting.edit_count}
            </Badge>
          )}
        </Group>

        {meeting.comment && (
          <Text size="sm" c="dimmed" truncate>
            {meeting.comment}
          </Text>
        )}

        <Group gap="md" mt={4}>
          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconClock size={12} />
              {formatDateTime(meeting.meeting_datetime)}
            </Group>
          </Text>

          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconUser size={12} />
              {participantNames}
            </Group>
          </Text>
        </Group>

        {/* Краткий итог если есть */}
        {meeting.summary && (
          <Text size="xs" c="green" mt={4} lineClamp={1}>
            Итог: {meeting.summary}
          </Text>
        )}
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={<IconPencil size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(meeting);
            }}
          >
            Редактировать
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={14} />}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(meeting);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const MeetingsWidget = ({ organization, onActivityChange }) => {
  const [meetings, setMeetings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Модальное окно формы
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingMeeting, setViewingMeeting] = useState(null);

  // Модальное окно итогов
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryMeeting, setSummaryMeeting] = useState(null);

  // Загрузка встреч
  const loadMeetings = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await meetingsApi.getMeetings(organization.id);
      setMeetings(data.items || []);
    } catch (err) {
      console.error('Failed to load meetings:', err);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  // Загрузка контактов для формы
  const loadContacts = useCallback(async () => {
    if (!organization?.id) return;

    try {
      const data = await meetingsApi.getContacts(organization.id);
      setContacts(data.items || []);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadMeetings();
    loadContacts();
  }, [loadMeetings, loadContacts]);

  // Открыть модальное окно для добавления
  const handleAddMeeting = () => {
    setEditingMeeting(null);
    setModalOpen(true);
  };

  // Открыть модальное окно для просмотра
  const handleViewMeeting = (meeting) => {
    setViewingMeeting(meeting);
    setViewModalOpen(true);
  };

  // Открыть модальное окно для редактирования
  const handleEditMeeting = (meeting) => {
    setViewModalOpen(false);
    setViewingMeeting(null);
    setEditingMeeting(meeting);
    setModalOpen(true);
  };

  // Открыть модальное окно для добавления итогов
  const handleAddSummary = (meeting) => {
    setViewModalOpen(false);
    setViewingMeeting(null);
    setSummaryMeeting(meeting);
    setSummaryModalOpen(true);
  };

  // Удалить встречу
  const handleDeleteMeeting = async (meeting) => {
    const contactNames = formatContactNames(meeting.contacts);
    if (!window.confirm(`Удалить встречу с "${contactNames}"?`)) {
      return;
    }

    try {
      await meetingsApi.deleteMeeting(organization.id, meeting.id);
      await loadMeetings();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to delete meeting:', err);
      alert(err.message);
    }
  };

  // Сохранить встречу (создание или обновление)
  const handleSubmit = async (data) => {
    setActionLoading(true);
    try {
      if (editingMeeting) {
        await meetingsApi.updateMeeting(organization.id, editingMeeting.id, data);
      } else {
        await meetingsApi.createMeeting(organization.id, data);
      }
      setModalOpen(false);
      setEditingMeeting(null);
      await loadMeetings();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // Сохранить итоги встречи
  const handleSaveSummary = async (summary) => {
    if (!summaryMeeting) return;

    setActionLoading(true);
    try {
      await meetingsApi.addSummary(organization.id, summaryMeeting.id, summary);
      setSummaryModalOpen(false);
      setSummaryMeeting(null);
      await loadMeetings();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to save summary:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <BaseWidget
        title="Встречи"
        icon={IconCalendarEvent}
        count={meetings.length}
        collapsible={true}
        defaultExpanded={meetings.length > 0}
        actions={
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleAddMeeting();
            }}
            disabled={contacts.length === 0}
          >
            Добавить
          </Button>
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : meetings.length > 0 ? (
          <Stack gap="xs">
            {meetings.map((meeting) => (
              <MeetingItem
                key={meeting.id}
                meeting={meeting}
                onView={handleViewMeeting}
                onEdit={handleEditMeeting}
                onDelete={handleDeleteMeeting}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconCalendarEvent
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Нет запланированных встреч
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {contacts.length > 0
                ? 'Запланируйте встречу с представителем'
                : 'Сначала добавьте контакты'}
            </Text>
            {contacts.length > 0 && (
              <Button
                variant="light"
                size="xs"
                mt="md"
                leftSection={<IconPlus size={14} />}
                onClick={handleAddMeeting}
              >
                Запланировать встречу
              </Button>
            )}
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно формы */}
      <MeetingFormModal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingMeeting(null);
        }}
        onSubmit={handleSubmit}
        meeting={editingMeeting}
        organizationId={organization?.id}
        contacts={contacts}
        loading={actionLoading}
      />

      {/* Модальное окно просмотра */}
      <MeetingViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingMeeting(null);
        }}
        meeting={viewingMeeting}
        onEdit={handleEditMeeting}
        onAddSummary={handleAddSummary}
      />

      {/* Модальное окно итогов */}
      <MeetingSummaryModal
        opened={summaryModalOpen}
        onClose={() => {
          setSummaryModalOpen(false);
          setSummaryMeeting(null);
        }}
        onConfirm={handleSaveSummary}
        loading={actionLoading}
        meeting={summaryMeeting}
      />
    </>
  );
};

export default MeetingsWidget;
